import { Link, Table, TableHead, TableBody, TableCell, TableRow, Checkbox } from '@mui/material'
import { ExportSettingsItem, setCsvByDocumentType } from 'api/rule'
import CsvConfigDialog from 'components/AppDialogs/CsvConfigDialog'
import { DocType, DOC_TYPE } from 'config'
import { useAlert } from 'hooks/useDialog'
import { useState, useCallback } from 'react'

export default function RuleCsvConfigList({
  list,
  reloadExportSettings,
}: {
  list?: ExportSettingsItem[]
  reloadExportSettings: () => Promise<void>
}) {
  const [selectedDocType, setSelectedDocType] = useState<DocType | null>(null)
  const [selectedDocTypeName, setSelectedDocTypeName] = useState('')
  const [selectedOcrConfigId, setSelectedOcrConfigId] = useState(0)
  const [openCsvConfigDialog, setOpenCsvConfigDialog] = useState(false)
  const [selectedSubGroup, setSelectedSubGroup] = useState<string | null>(null)
  const [saving, setSaving] = useState(-1)
  const alert = useAlert()

  const handleOpenDialog = useCallback(
    (docType: DocType, docTypeName: string, subGroup?: string, ocrBuilderConfigID?: number) => {
      setSelectedOcrConfigId(ocrBuilderConfigID || 0)
      setSelectedDocType(docType)
      setSelectedSubGroup(subGroup || null)
      setSelectedDocTypeName(docTypeName)
      setOpenCsvConfigDialog(true)
    },
    [],
  )

  const handleUpdateExportType = useCallback(
    async (ocrBuilderId: number, csvByDocumentType: boolean) => {
      setSaving(ocrBuilderId)
      try {
        await setCsvByDocumentType(ocrBuilderId, csvByDocumentType)
      } catch {
        alert({ message: '更新処理が失敗しました' })
      }
      await reloadExportSettings()
      setSaving(-1)
    },
    [alert, reloadExportSettings],
  )

  return (
    <div>
      <Table size="small" sx={{ mt: 2, width: 'unset', minWidth: 600 }}>
        <TableHead data-testid="table-head-export-settings">
          <TableRow
            sx={{
              '& th': {
                whiteSpace: 'nowrap',
                backgroundColor: 'grey.200',
              },
            }}
          >
            <TableCell>文書タイプ名</TableCell>
            <TableCell>グループ</TableCell>
            <TableCell>出力形式</TableCell>
            <TableCell>文書種別毎/CSV出力</TableCell>
          </TableRow>
        </TableHead>
        <TableBody data-testid="table-body-export-settings">
          {(list || []).map((config) => (
            <TableRow key={`${config.subGroup}${config.documentType}`}>
              <TableCell component="th" scope="row">
                <Link
                  align="left"
                  color="inherit"
                  underline="always"
                  component="button"
                  onClick={() =>
                    handleOpenDialog(
                      config.documentType,
                      config.documentName,
                      config.subGroup || undefined,
                      config.ocrBuilderConfigID,
                    )
                  }
                >
                  {config.documentName}
                </Link>
              </TableCell>
              <TableCell>{config.subGroup}</TableCell>
              <TableCell>{config.exportFormat}</TableCell>
              <TableCell>
                <Checkbox
                  disabled={
                    saving === config.ocrBuilderConfigID ||
                    config.documentType !== DOC_TYPE.OCR_BUILDER
                  }
                  checked={config.csvByDocumentType}
                  color="primary"
                  onClick={() =>
                    handleUpdateExportType(config.ocrBuilderConfigID, !config.csvByDocumentType)
                  }
                />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      {(!list || list.length === 0) && '出力設定がありません'}
      <CsvConfigDialog
        docType={selectedDocType || DOC_TYPE.INVOICE}
        docTypeName={selectedDocTypeName}
        ocrBuilderConfigID={selectedOcrConfigId}
        open={openCsvConfigDialog}
        subGroup={selectedSubGroup || undefined}
        onClose={(canceled) => [
          canceled ? undefined : reloadExportSettings(),
          setOpenCsvConfigDialog(false),
        ]}
      />
    </div>
  )
}
